import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { getApiUrl } from "../utils/apiUtils";
import LanguageSelector from "../components/LanguageSelector";

const VerifyAccount = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const apiUrl = getApiUrl();
  const { t } = useTranslation();

  // Track component state: 'loading', 'success', or 'error'
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    document.title = t("Verify Account | KhayatGo");

    if (!token) {
      toast.warn(t("Invalid or missing verification token."), {
        toastId: "invalid-token",
      });
      navigate("/users/login");
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await axios.post(`${apiUrl}/users/verify-account`, {
          token,
        });
        toast.success(response.data.message, {
          toastId: "successful-verification",
        });
        setStatus("success");
      } catch (error) {
        console.error("Verification error:", error);
        const errorMsg =
          error.response?.data?.message || t("Failed to verify account.");
        toast.error(errorMsg, { toastId: "verification-error" });
        setStatus("error");

        // Redirect to login after a short delay on failure
        setTimeout(() => navigate("/users/login"), 3000);
      }
    };

    verifyToken();
  }, [token, navigate, apiUrl, t]);

  return (
    <>
      <LanguageSelector />
      <main className="flex flex-col md:flex-row md:h-screen">
        {/* Banner Section matching Login.jsx */}
        <section className="bg-login-banner w-full md:w-[50%] min-h-[50vh] relative hidden md:block"></section>

        {/* Verification Status Section */}
        <section className="w-full md:w-[50%] px-[1rem] py-[8rem] flex flex-col items-center justify-center md:h-screen bg-[#ddd] overflow-y-auto">
          <div className="bg-white p-8 rounded-lg shadow-md w-full md:w-4/5 text-center flex flex-col items-center gap-6">
            <h1 className="text-[2rem] font-minimoBold">
              {t("Account Verification")}
            </h1>

            {status === "loading" && (
              <p className="text-lg text-gray-700 animate-pulse">
                {t("Verifying your account, please wait...")}
              </p>
            )}

            {status === "success" && (
              <div className="flex flex-col items-center gap-4 w-full">
                <p className="text-lg text-green-600 font-medium">
                  {t("Your account has been successfully verified!")}
                </p>
                <Link
                  to="/users/login"
                  className="bg-black text-white py-3 px-6 w-full rounded-md hover:bg-gray-800 transition-all font-medium uppercase tracking-wide"
                >
                  {t("Proceed to Login")}
                </Link>
              </div>
            )}

            {status === "error" && (
              <div className="flex flex-col items-center gap-4">
                <p className="text-lg text-red-600 font-medium">
                  {t("Verification failed or your link has expired.")}
                </p>
                <p className="text-sm text-gray-500">
                  {t("Redirecting to the login page...")}
                </p>
              </div>
            )}
          </div>
        </section>
      </main>

      <footer className="fixed bottom-0 left-0 w-full bg-slate-800 p-1 z-10">
        <p className="text-center text-white">
          {t("To learn more about us, please")}{" "}
          <Link to="/about" className="underline hover:text-gray-300">
            {t("click here")}
          </Link>
          .
        </p>
      </footer>
    </>
  );
};

export default VerifyAccount;
